import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowRight, MapPin, Globe, Mic2 } from "lucide-react";
import { Link } from "react-router-dom";
import CTABand from "../components/shared/CTABand";
import { IMAGES } from "../data/images";

function RevealSection({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 32 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }} className={className}>
      {children}
    </motion.div>
  );
}

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="page-hero" style={{ backgroundColor: "var(--surface)" }} data-testid="about-hero">
        <div className="container-xl">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="overline-label mb-4">About Core AI Summit + Tradeshow</div>
            <h1 className="text-5xl sm:text-6xl font-black text-white mb-6 max-w-3xl"
              style={{ fontFamily: "var(--font-heading)" }}>
              Built in Canada.<br />Connected to the world.
            </h1>
            <p className="text-lg max-w-2xl" style={{ color: "var(--text-muted)" }}>
              Core AI Summit + Tradeshow is being created to connect Canada's extraordinary AI research and technology ecosystem with the businesses, governments, investors and international partners that can help turn Canadian innovation into economic opportunity.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission */}
      <section className="section-py" data-testid="about-mission-section">
        <div className="container-xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <RevealSection>
              <div className="overline-label mb-4">Our Purpose</div>
              <h2 className="text-4xl font-black text-white mb-6 leading-tight"
                style={{ fontFamily: "var(--font-heading)" }}>
                AI is moving fast. Most organizations are still finding their way.
              </h2>
              <p className="text-base leading-relaxed mb-5" style={{ color: "var(--text-muted)" }}>
                Artificial intelligence is transforming how organizations operate, compete and grow. But for many business leaders, the biggest challenge isn't understanding that AI matters — it's knowing where to begin.
              </p>
              <p className="text-base leading-relaxed" style={{ color: "var(--text-muted)" }}>
                Core AI Summit + Tradeshow brings together researchers, technology companies, business leaders, government, investors and innovators to connect what is being developed with the organizations that need to understand and deploy it.
              </p>
            </RevealSection>
            <RevealSection delay={0.15}>
              <div className="space-y-4">
                {[
                  { num: "01", title: "Conference", desc: "Programming organized around eight pillars covering AI adoption, emerging technology, infrastructure, sovereignty, workforce and more." },
                  { num: "02", title: "Trade Show", desc: "A full exhibition floor where AI companies, technology providers and solution builders demonstrate what they are building." },
                  { num: "03", title: "Workshops", desc: "Practical, hands-on learning designed to turn uncertainty into action for organizations at every stage of AI adoption." },
                  { num: "04", title: "Demonstrations", desc: "Live technology demonstrations that allow attendees to see AI in action beyond the presentation room." },
                ].map((item) => (
                  <div key={item.num} className="p-5 rounded-xl" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
                    <div className="flex items-start gap-4">
                      <div className="overline-label" style={{ flexShrink: 0, marginTop: 2 }}>{item.num}</div>
                      <div>
                        <div className="font-bold text-white mb-1" style={{ fontFamily: "var(--font-heading)" }}>{item.title}</div>
                        <div className="text-sm" style={{ color: "var(--text-muted)" }}>{item.desc}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </RevealSection>
          </div>
        </div>
      </section>

      {/* Founder */}
      <section id="founder" className="section-py" style={{ backgroundColor: "var(--surface)" }} data-testid="about-founder-section">
        <div className="container-xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <RevealSection delay={0.1}>
              <div className="relative">
                <div className="rounded-2xl overflow-hidden h-80 flex items-center justify-center"
                  style={{ background: "linear-gradient(135deg, rgba(0,212,255,0.08) 0%, rgba(123,47,190,0.08) 100%)", border: "1px solid var(--border)" }}>
                  <div className="text-center p-8">
                    <div className="w-24 h-24 rounded-full mx-auto mb-5 flex items-center justify-center"
                      style={{ background: "rgba(0,212,255,0.1)", border: "2px solid rgba(0,212,255,0.3)" }}>
                      <Mic2 size={36} style={{ color: "var(--cyan)" }} />
                    </div>
                    <div className="text-xl font-black text-white mb-1" style={{ fontFamily: "var(--font-heading)" }}>Randy Rowe</div>
                    <div className="text-sm" style={{ color: "var(--cyan)", fontFamily: "var(--font-mono)" }}>President & Founder</div>
                    <div className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>Core AI Summit + Tradeshow</div>
                  </div>
                </div>
              </div>
            </RevealSection>
            <RevealSection>
              <div>
                <div className="overline-label mb-4">About the Founder</div>
                <h2 className="text-4xl font-black text-white mb-6"
                  style={{ fontFamily: "var(--font-heading)" }}>Randy Rowe</h2>
                <div className="space-y-4 text-base leading-relaxed" style={{ color: "var(--text-muted)" }}>
                  <p>
                    Randy Rowe has spent more than 20 years producing conferences, exhibitions and live events in Canada.
                  </p>
                  <p>
                    A programmer at heart, Randy has worked with computers and programming since 1985 and has spent his career following how emerging technologies move from innovation into widespread adoption.
                  </p>
                  <p>
                    After attending major AI events including Ai4 in Las Vegas, the Databricks Data + AI Summit in San Francisco and ALL IN in Montréal, he saw both the extraordinary potential of artificial intelligence and a significant challenge: many organizations know AI will affect their future but still don't know where to begin.
                  </p>
                  <p style={{ color: "var(--text)" }}>
                    Core AI Summit + Tradeshow was created to help bridge that gap.
                  </p>
                </div>
              </div>
            </RevealSection>
          </div>
        </div>
      </section>

      {/* Canadian positioning */}
      <section className="section-py" data-testid="about-canada-section">
        <div className="container-xl">
          <RevealSection>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: Globe,
                  title: "Canadian AI Leadership",
                  desc: "Canada helped pioneer modern artificial intelligence. Core AI Summit creates a meeting place for Canadian AI companies, researchers and institutions while building connections with technology ecosystems around the world.",
                },
                {
                  icon: MapPin,
                  title: "Why Niagara Falls",
                  desc: "An international destination at the intersection of technology, commerce and power. Approximately 130 million people live within a one-day drive — creating extraordinary access to Canadian and U.S. companies, investors and attendees.",
                },
                {
                  icon: ArrowRight,
                  title: "International in Reach",
                  desc: "Located minutes from the Canada–U.S. border, Core AI Summit will create a natural meeting place for Canadian and American technology ecosystems while welcoming delegations from around the world.",
                },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <RevealSection key={item.title} delay={i * 0.1}>
                    <div className="card card-glow p-7 h-full">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-5"
                        style={{ background: "rgba(0,212,255,0.08)", border: "1px solid rgba(0,212,255,0.15)" }}>
                        <Icon size={18} style={{ color: "var(--cyan)" }} />
                      </div>
                      <h4 className="font-black text-white mb-3" style={{ fontFamily: "var(--font-heading)" }}>{item.title}</h4>
                      <p className="text-sm leading-relaxed" style={{ color: "var(--text-muted)" }}>{item.desc}</p>
                    </div>
                  </RevealSection>
                );
              })}
            </div>
          </RevealSection>
        </div>
      </section>

      <CTABand
        title="Be Part of Core AI Summit + Tradeshow"
        subtitle="Join Canada's AI conference and trade show in Niagara Falls, Ontario — June 2027."
        primaryLabel="Join Priority List"
        secondaryLabel="Partner With Us"
        secondaryHref="/partner"
      />
    </div>
  );
}
